import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function TrustSafety() {
  const safetyFeatures = [
    {
      icon: 'ri-shield-check-line',
      title: 'Identity Verification',
      description: 'All users must verify their identity with government-issued ID and phone number before booking or hosting.',
    },
    {
      icon: 'ri-file-shield-line',
      title: 'Comprehensive Insurance',
      description: 'Up to $1 million in property damage coverage and $5 million in liability protection for all bookings.',
    },
    {
      icon: 'ri-star-line',
      title: 'Review System',
      description: 'Transparent two-way reviews help build trust and accountability in our community.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 Support',
      description: 'Our dedicated support team is available around the clock to assist with any safety concerns.',
    },
    {
      icon: 'ri-lock-line',
      title: 'Secure Payments',
      description: 'All transactions are processed through encrypted payment systems. We never share your financial information.',
    },
    {
      icon: 'ri-camera-line',
      title: 'Photo Verification',
      description: 'Hosts must provide accurate photos of their spaces, and we verify listings to ensure authenticity.',
    },
  ];

  const renterGuidelines = [
    {
      title: 'Before Booking',
      items: [
        'Read the space description and host rules carefully',
        'Check host reviews and ratings',
        'Ask questions through our messaging system',
        'Verify the space meets your storage needs',
        'Review the cancellation policy',
      ],
    },
    {
      title: 'During Your Rental',
      items: [
        'Document the condition of your items with photos',
        'Follow all access rules and schedules',
        'Keep your items properly secured',
        'Report any issues immediately to the host and Stashly',
        'Respect the property and neighboring spaces',
      ],
    },
    {
      title: 'Safety Tips',
      items: [
        'Never store prohibited items (hazardous materials, illegal items, etc.)',
        'Use quality locks and security measures',
        'Keep an inventory of stored items',
        'Consider additional insurance for high-value items',
        'Meet hosts in person when possible before booking',
      ],
    },
  ];

  const hostGuidelines = [
    {
      title: 'Preparing Your Space',
      items: [
        'Ensure the space is clean, dry, and secure',
        'Provide accurate measurements and photos',
        'Install proper lighting and security features',
        'Clear any hazards or obstacles',
        'Provide clear access instructions',
      ],
    },
    {
      title: 'Screening Renters',
      items: [
        'Review renter profiles and ratings',
        'Communicate clearly about your rules and expectations',
        'Ask about what items will be stored',
        'Set clear access schedules',
        'Document the space condition before rental',
      ],
    },
    {
      title: 'During the Rental',
      items: [
        'Maintain the agreed-upon access schedule',
        'Keep the space secure and weather-protected',
        'Respond promptly to renter concerns',
        'Report any issues to Stashly immediately',
        'Respect renter privacy and belongings',
      ],
    },
  ];

  const prohibitedItems = [
    'Hazardous or flammable materials',
    'Illegal substances or items',
    'Weapons or ammunition',
    'Perishable food items',
    'Live animals or plants',
    'Stolen property',
    'Items that emit strong odors',
    'Toxic or radioactive materials',
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white py-24 pt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full text-5xl mx-auto mb-6">
            <i className="ri-shield-check-line"></i>
          </div>
          <h1 className="text-5xl font-bold mb-6">Trust &amp; Safety</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Your safety and security are our top priorities. Learn about the measures we take to protect our community.
          </p>
        </div>
      </section>

      {/* Safety Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How We Keep You Safe</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple layers of protection to ensure a secure experience for everyone
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {safetyFeatures.map((feature, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 text-blue-600 rounded-xl text-3xl mb-6">
                  <i className={feature.icon}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Renter Guidelines */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Safety Guidelines for Renters</h2>
            <p className="text-xl text-gray-600">
              Follow these best practices to ensure a safe storage experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {renterGuidelines.map((section, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{section.title}</h3>
                <ul className="space-y-4">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-0.5"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Host Guidelines */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Safety Guidelines for Hosts</h2>
            <p className="text-xl text-gray-600">
              Protect your property and provide a secure space for renters
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {hostGuidelines.map((section, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{section.title}</h3>
                <ul className="space-y-4">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-0.5"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prohibited Items */}
      <section className="py-20 bg-red-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 flex items-center justify-center bg-red-600 text-white rounded-full text-3xl mx-auto mb-6">
              <i className="ri-forbid-line"></i>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Prohibited Items</h2>
            <p className="text-xl text-gray-600">
              The following items are strictly forbidden on Stashly
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {prohibitedItems.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
                  <i className="ri-close-circle-fill text-red-600 text-2xl"></i>
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Information */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#14B8A6] to-[#0F9B8E] text-white rounded-2xl p-12">
            <div className="text-center mb-8">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full text-3xl mx-auto mb-6">
                <i className="ri-shield-star-line"></i>
              </div>
              <h2 className="text-4xl font-bold mb-4">Insurance Coverage</h2>
              <p className="text-xl text-white/90">
                Every booking is protected by comprehensive insurance
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4">Property Damage</h3>
                <p className="text-white/90 mb-4">Up to $1,000,000 coverage for damage to the host\'s property</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl mt-0.5"></i>
                    <span className="text-white/90">Structural damage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl mt-0.5"></i>
                    <span className="text-white/90">Water damage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl mt-0.5"></i>
                    <span className="text-white/90">Fire damage</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-2xl font-bold mb-4">Liability Protection</h3>
                <p className="text-white/90 mb-4">Up to $5,000,000 in liability coverage for hosts</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl mt-0.5"></i>
                    <span className="text-white/90">Bodily injury</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl mt-0.5"></i>
                    <span className="text-white/90">Property damage to third parties</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl mt-0.5"></i>
                    <span className="text-white/90">Legal defense costs</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 text-center">
              <a
                href="/contact"
                className="inline-block px-8 py-4 bg-white text-[#14B8A6] font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Learn More About Coverage
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Report Issues */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-red-600 text-white rounded-full text-3xl mx-auto mb-6">
            <i className="ri-alarm-warning-line"></i>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Report a Safety Concern</h2>
          <p className="text-xl text-gray-600 mb-8">
            If you encounter any safety issues or suspicious activity, please contact us immediately
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-block px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-alert-line mr-2"></i>
              Report an Issue
            </a>
            <a
              href="/help-center"
              className="inline-block px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg border-2 border-gray-300 hover:border-gray-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-question-line mr-2"></i>
              Visit Help Centre
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
